import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Download, Clock, User, Calendar, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { madridTime } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABELS = {
  digital: { label: 'Digital', color: 'bg-blue-100 text-blue-700' },
  manual: { label: 'Manual', color: 'bg-amber-100 text-amber-700' },
  edited: { label: 'Editado', color: 'bg-purple-100 text-purple-700' },
};

const TimeClockPublicView = () => {
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/timeclock/public/employees`)
      .then(r => r.json()).then(setEmployees).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ month: selectedMonth });
    if (selectedEmployee !== 'all') params.set('employee_id', selectedEmployee);
    fetch(`${API_URL}/api/timeclock/public/records?${params}`)
      .then(r => r.json()).then(data => { setRecords(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedEmployee, selectedMonth]);

  const summary = useMemo(() => {
    const byEmp = {};
    records.forEach(r => {
      const eid = r.employee_id;
      if (!byEmp[eid]) byEmp[eid] = { name: r.employee_name, hours: 0, days: 0 };
      if (r.total_hours) { byEmp[eid].hours += r.total_hours; byEmp[eid].days += 1; }
    });
    return Object.values(byEmp).map(e => ({ ...e, hours: Math.round(e.hours * 100) / 100 }));
  }, [records]);

  const changeMonth = (delta) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const names = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${names[m - 1]} ${y}`;
  })();

  const downloadCSV = async () => {
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      if (selectedEmployee !== 'all') params.set('employee_id', selectedEmployee);
      const res = await fetch(`${API_URL}/api/timeclock/public/export-csv?${params}`);
      const data = await res.json();
      const blob = new Blob([data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = data.filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Descargado: ${data.filename}`);
    } catch { toast.error('Error al descargar'); }
  };

  const downloadPDF = async () => {
    try {
      const params = new URLSearchParams({ month: selectedMonth });
      if (selectedEmployee !== 'all') params.set('employee_id', selectedEmployee);
      const res = await fetch(`${API_URL}/api/timeclock/public/export-pdf?${params}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'No hay registros para generar PDF');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `registro_jornada_${selectedMonth}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch { toast.error('Error al generar PDF'); }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="timeclock-public-view">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Clock size={24} className="text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">Control Horario</h1>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={downloadPDF} className="gap-2" data-testid="download-pdf-btn">
            <FileText size={16} /> PDF
          </Button>
          <Button variant="outline" onClick={downloadCSV} className="gap-2" data-testid="download-csv-btn">
            <Download size={16} /> CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 bg-white rounded-xl border px-2">
          <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="prev-month-btn">
            <ChevronLeft size={18} />
          </button>
          <span className="font-semibold text-gray-800 min-w-[140px] text-center" data-testid="current-month">{monthLabel}</span>
          <button onClick={() => changeMonth(1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="next-month-btn">
            <ChevronRight size={18} />
          </button>
        </div>
        <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
          <SelectTrigger className="w-[180px]" data-testid="employee-filter">
            <User size={16} className="mr-2 text-gray-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      {summary.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {summary.map(s => (
            <div key={s.name} className="bg-white border rounded-xl p-4" data-testid={`summary-card-${s.name}`}>
              <p className="text-sm text-gray-500 font-medium">{s.name}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{s.hours}h</p>
              <p className="text-xs text-gray-400">{s.days} días trabajados</p>
            </div>
          ))}
        </div>
      )}

      {/* Records table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <FileText size={16} className="text-gray-400" />
          <h2 className="font-semibold text-gray-700">Registros</h2>
          <span className="text-sm text-gray-400 ml-auto">{records.length} registros</span>
        </div>
        <ScrollArea className="max-h-[500px]">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Cargando...</div>
          ) : records.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <Calendar size={36} className="mx-auto mb-2 opacity-40" />
              <p>Sin registros para este periodo</p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="records-table">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Fecha</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Empleado</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Entrada</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Salida</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-gray-600">Horas</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-gray-600">Origen</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => {
                  const source = SOURCE_LABELS[r.source] || SOURCE_LABELS.digital;
                  const ci = madridTime(r.clock_in, '—');
                  const co = madridTime(r.clock_out, '—');
                  return (
                    <tr key={r.id || idx} className="border-t hover:bg-gray-50/50">
                      <td className="px-4 py-2.5 font-medium text-gray-800">{r.date}</td>
                      <td className="px-4 py-2.5 text-gray-700">{r.employee_name}</td>
                      <td className="px-4 py-2.5 text-gray-600">{ci}</td>
                      <td className="px-4 py-2.5 text-gray-600">{co}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-800">{r.total_hours ? `${r.total_hours}h` : '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${source.color}`}>{source.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default TimeClockPublicView;
